import { useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

interface ScrollableColumnContainerProps {
  children: ReactNode;
  className?: string;
}

export function ScrollableColumnContainer({
  children,
  className = "",
}: ScrollableColumnContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 15;
    setCanScrollDown(hasMore);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });

    resizeObserver.observe(el);

    const mutationObserver = new MutationObserver(() => {
      checkScroll();
    });

    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [children, checkScroll]);

  const handleScrollDown = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({
      top: Math.max(el.clientHeight * 0.75, 200),
      behavior: "smooth",
    });
  };

  return (
    <div className="relative flex-1 min-h-0 w-full overflow-hidden isolate">
      <div
        ref={containerRef}
        onScroll={checkScroll}
        className={`h-full overflow-y-auto no-scrollbar ${className}`}
      >
        {children}
      </div>

      {canScrollDown && (
        <>
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-stone-100/90 dark:from-stone-900/90 to-transparent z-[1] transition-opacity" />
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[2] pointer-events-none animate-in fade-in duration-200">
            <button
              type="button"
              onClick={handleScrollDown}
              className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-900/90 dark:bg-stone-100/90 text-white dark:text-stone-900 text-[11px] font-medium shadow-md backdrop-blur-xs hover:bg-stone-800 dark:hover:bg-white hover:scale-105 active:scale-95 transition-all cursor-pointer"
              title="More items below (click to scroll)"
            >
              <span>More items</span>
              <ChevronDown className="w-3.5 h-3.5 animate-bounce" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
